/**
 * Map a notification → destination route for deep-linking.
 *
 * The resolver looks at three sources in order:
 *   1. `notification.data.route`  – explicit override (FCM data payload)
 *   2. `notification.type`        – n_type set by the backend push call
 *   3. title / message keywords   – fallback for legacy "system" notifications
 *      that haven't been re-tagged yet.
 *
 * Returns `null` when nothing matches — the caller can decide to fall through
 * to /app/notifications.
 *
 * `userRole` is optional and used to pick the right destination for ambiguous
 * categories (e.g. iprint shop owners go to the shop console, customers to the
 * customer hub).
 */
const TYPE_ROUTES = {
  // Tickets / events
  ticket: "/app/tickets",
  event_ticket: "/app/tickets",
  event_entry: "/app/tickets",

  // Wallet / payments
  wallet: "/app/wallet",
  topup: "/app/wallet",
  payment: "/app/transactions",
  card_scanned: "/app/transactions",

  // Payouts
  payout: "/app/payouts",
  payout_decision: "/app/payouts",

  // Merchant
  card_sale: "/app/merchant",
  activation_qr_sale: "/app/merchant",
  cash_sale: "/app/merchant",
  recharge: "/app/merchant/float",
  float_topup: "/app/merchant/float",

  // iPrint
  print_job: "/app/iprint",
  print_complete: "/app/iprint",
  print_shop: "/app/iprint/shop",

  // Transport
  transport: "/app/tickets",
  transport_booking: "/app/tickets",
  transport_trip: "/app/transport/conductor",
  conductor_joined: "/app/transport/conductor",

  // Connect / social
  connection: "/app/connect",
  message: "/app/connect",
  profile_viewed: "/app/connect",

  // Driver / workers
  worker: "/app/workers",
  pin_reset: "/app/profile",

  // Admin queues
  profile_update_request: "/app/admin/profile-updates",
  unblock_request: "/app/admin/unblock-requests",
};

// Keyword fallback for legacy "system" notifications — match the visible title.
const KEYWORD_ROUTES = [
  { rx: /payout/i,                                route: "/app/payouts" },
  { rx: /trip (fare )?paid|welcome aboard|seat reserved|conductor joined/i, route: "/app/tickets" },
  { rx: /\btrip\b|\btransport\b|conductor/i,      route: (role) => role === "conductor" || role === "driver" ? "/app/transport/conductor" : "/app/tickets" },
  { rx: /new print job|print complete|print queued|printed your file/i,    route: (role) => role === "iprint_shop" ? "/app/iprint/shop" : "/app/iprint" },
  { rx: /card sale|cash sale|sold .*(card|icaard)|commission/i, route: "/app/merchant" },
  { rx: /wallet recharged|recharged at|recharge successful|float topped up/i, route: (role) => role === "merchant" ? "/app/merchant/float" : "/app/wallet" },
  { rx: /top-?up (confirmed|successful)/i,        route: "/app/wallet" },
  { rx: /payment (sent|received|charged)|sale completed/i, route: "/app/transactions" },
  { rx: /entry granted|ticket confirmed|checked in|you have been checked in/i, route: "/app/tickets" },
  { rx: /profile update request/i,                route: (role) => role === "admin" ? "/app/admin/profile-updates" : "/app/profile" },
  { rx: /(profile update|kyc).*?(approved|denied|request)/i, route: "/app/profile" },
  { rx: /staff joined|event assigned/i,           route: "/app/check-in" },
  { rx: /(new )?connection|connection (accepted|declined)|message from/i, route: "/app/connect" },
  { rx: /icaard was scanned|someone viewed your profile/i, route: "/app/connect" },
  { rx: /welcome.*student|student status updated/i, route: "/app/profile" },
  { rx: /icaard (replaced|now active)|login reset|password reset/i, route: "/app/profile" },
];

export function resolveNotificationRoute(notification, userRole = "") {
  if (!notification) return null;
  const data = notification.data || notification.metadata || {};

  // 1) Explicit override
  if (data.route && typeof data.route === "string" && data.route.startsWith("/")) {
    return data.route;
  }

  // 2) Backend-set type
  const t = (notification.type || "").toLowerCase();
  if (t && TYPE_ROUTES[t]) return TYPE_ROUTES[t];

  // 3) Keyword sniff on title + message
  const haystack = `${notification.title || ""} ${notification.message || notification.body || ""}`;
  for (const { rx, route } of KEYWORD_ROUTES) {
    if (rx.test(haystack)) {
      return typeof route === "function" ? route(userRole) : route;
    }
  }

  return null;
}
